/**
 * Code Health API layer — typed client + React Query hooks for the
 * `/v1/repos/:id/code-health/*` routes and `/v1/quality-findings`.
 *
 * Entitlement: Pro plan only. The API returns HTTP 402 when the
 * caller's org is on Free. The hooks surface that as a typed
 * `entitlement: "required"` flag so the tab can render the upsell
 * card instead of a generic error state.
 */

import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { apiFetch, ApiError } from "./client";

// ─── Payload types ────────────────────────────────────────────────

export type OverviewTile<T> = T & { updated_at: string | null };

export interface CodeHealthOverview {
  repo_id: string;
  tiles: {
    technical_debt: OverviewTile<{
      sqale_minutes: number | null;
      code_smells: number | null;
    }> | null;
    complexity: OverviewTile<{
      top_churn_files: number;
      open_quality_findings: number;
    }>;
    stale: OverviewTile<{
      stale_pct_files: number | null;
      stale_pct_loc: number | null;
      cutoff_date: string | null;
    }> | null;
    frameworks: OverviewTile<{
      fragmentation_score: number | null;
      framework_count: number;
    }> | null;
  };
}

export interface ChurnedFile {
  path: string;
  commits: number;
  lines_changed?: number;
  unique_authors?: number;
  last_commit?: string;
}

export interface BusFactorFile {
  path: string;
  unique_authors: number;
  sole_author?: string;
  commits?: number;
}

export interface CommitHistoryPayload {
  window_days?: number;
  total_commits?: number;
  commits_by_month?: Record<string, number>;
  top_churned_files?: ChurnedFile[];
  bus_factor_files?: BusFactorFile[];
  commit_message_quality?: {
    conventional_commits_ratio?: number;
    avg_length?: number;
    fixup_ratio?: number;
  };
}

export interface CommitHistoryResponse {
  repo_id: string;
  commit_sha: string;
  computed_at: string;
  payload: CommitHistoryPayload;
}

export interface StaleFile {
  path: string;
  last_modified: string;
  loc?: number;
  last_author_hash?: string;
}

export interface StaleFilesResponse {
  repo_id: string;
  commit_sha: string;
  computed_at: string;
  summary: {
    cutoff_date: string | null;
    total_files: number | null;
    stale_count: number | null;
    stale_pct_files: number | null;
    stale_pct_loc: number | null;
  };
  files: StaleFile[];
  pagination: { limit: number; offset: number; total: number };
}

export interface Framework {
  name: string;
  version_guess?: string;
  loc?: number;
  pct?: number;
  dirs?: string[];
}

export interface FrameworksPayload {
  frameworks: Framework[];
  fragmentation_score: number;
  partial_usage_signals?: Array<{ signal: string; file?: string; line?: number }>;
}

export interface FrameworksResponse {
  repo_id: string;
  commit_sha: string;
  computed_at: string;
  payload: FrameworksPayload;
}

export interface QualityFinding {
  id: string;
  fingerprint: string;
  status: string;
  severity: string;
  tool: string;
  title: string;
  category: "quality";
  quality_metric: {
    name?: string;
    value?: number;
    threshold?: number;
    unit?: string;
  } | null;
  location_data: {
    file_path?: string;
    line?: number;
    rule_id?: string;
    kind?: string;
  } & Record<string, unknown>;
  first_seen_at: string;
  last_seen_at?: string;
  fixed_at?: string | null;
  suppressed_at?: string | null;
}

export interface QualityFindingsResponse {
  repo_id: string;
  findings: QualityFinding[];
  pagination: { limit: number; offset: number; total: number };
}

// ─── Fetchers ─────────────────────────────────────────────────────

export async function getCodeHealthOverview(repoId: string) {
  return apiFetch<CodeHealthOverview>({
    path: `/v1/repos/${repoId}/code-health/overview`,
  });
}

export async function getCommitHistory(repoId: string) {
  return apiFetch<CommitHistoryResponse>({
    path: `/v1/repos/${repoId}/code-health/commit-history`,
  });
}

export async function getStaleFiles(
  repoId: string,
  opts?: { limit?: number; offset?: number },
) {
  const params = new URLSearchParams();
  if (opts?.limit != null) params.set("limit", String(opts.limit));
  if (opts?.offset != null) params.set("offset", String(opts.offset));
  const qs = params.toString();
  return apiFetch<StaleFilesResponse>({
    path: `/v1/repos/${repoId}/code-health/stale-files${qs ? `?${qs}` : ""}`,
  });
}

export async function getFrameworks(repoId: string) {
  return apiFetch<FrameworksResponse>({
    path: `/v1/repos/${repoId}/code-health/frameworks`,
  });
}

export interface SonarAnalysis {
  id: string;
  sonar_project_key: string;
  sonar_analysis_id: string | null;
  status: "pending" | "running" | "completed" | "failed" | "timeout";
  error: string | null;
  metrics: Record<string, number | string>;
  issues_count: number;
  started_at: string;
  completed_at: string | null;
  commit_sha: string | null;
}

export interface SonarResponse {
  repo_id: string;
  analysis: SonarAnalysis;
}

export async function getSonarAnalysis(repoId: string) {
  return apiFetch<SonarResponse>({
    path: `/v1/repos/${repoId}/code-health/sonar`,
  });
}

export async function listQualityFindings(
  repoId: string,
  opts?: {
    rule_id?: string;
    kind?: string;
    severity?: string;
    status?: string;
    file_path_contains?: string;
    limit?: number;
    offset?: number;
  },
) {
  const params = new URLSearchParams({ repo_id: repoId });
  if (opts) {
    for (const [k, v] of Object.entries(opts)) {
      if (v != null) params.set(k, String(v));
    }
  }
  return apiFetch<QualityFindingsResponse>({
    path: `/v1/quality-findings?${params.toString()}`,
  });
}

// ─── Query keys ───────────────────────────────────────────────────

export const codeHealthKeys = {
  overview: (id: string) => ["codeHealth", "overview", id] as const,
  commitHistory: (id: string) => ["codeHealth", "commitHistory", id] as const,
  staleFiles: (id: string, opts?: Record<string, unknown>) =>
    ["codeHealth", "staleFiles", id, opts ?? {}] as const,
  frameworks: (id: string) => ["codeHealth", "frameworks", id] as const,
  qualityFindings: (id: string, opts?: Record<string, unknown>) =>
    ["codeHealth", "qualityFindings", id, opts ?? {}] as const,
  sonar: (id: string) => ["codeHealth", "sonar", id] as const,
};

// ─── Entitlement helpers ──────────────────────────────────────────

export interface EntitlementInfo {
  required: boolean;
  currentPlan?: string;
  upgradeUrl?: string;
  message?: string;
}

/** Extract payment-required info from a thrown ApiError (402). */
export function entitlementFromError(err: unknown): EntitlementInfo | null {
  if (err instanceof ApiError && err.status === 402) {
    const body = (err.body ?? {}) as {
      current_plan?: string;
      upgrade_url?: string;
      message?: string;
    };
    return {
      required: true,
      currentPlan: body.current_plan,
      upgradeUrl: body.upgrade_url ?? "/billing",
      message: body.message,
    };
  }
  return null;
}

// Shared options: don't retry 402/403 — retrying the entitlement
// gate achieves nothing and delays the upsell.
const dontRetryOnAuth = (failureCount: number, error: unknown) => {
  if (error instanceof ApiError && (error.status === 402 || error.status === 403 || error.status === 404)) {
    return false;
  }
  return failureCount < 2;
};

// ─── Hooks ────────────────────────────────────────────────────────

export function useCodeHealthOverview(
  repoId: string,
  opts?: Partial<UseQueryOptions<CodeHealthOverview>>,
) {
  return useQuery({
    queryKey: codeHealthKeys.overview(repoId),
    queryFn: () => getCodeHealthOverview(repoId),
    enabled: Boolean(repoId),
    staleTime: 60_000,
    retry: dontRetryOnAuth,
    ...opts,
  });
}

export function useCommitHistory(repoId: string) {
  return useQuery({
    queryKey: codeHealthKeys.commitHistory(repoId),
    queryFn: () => getCommitHistory(repoId),
    enabled: Boolean(repoId),
    staleTime: 60_000,
    retry: dontRetryOnAuth,
  });
}

export function useStaleFiles(
  repoId: string,
  opts?: { limit?: number; offset?: number },
) {
  return useQuery({
    queryKey: codeHealthKeys.staleFiles(repoId, opts),
    queryFn: () => getStaleFiles(repoId, opts),
    enabled: Boolean(repoId),
    staleTime: 60_000,
    retry: dontRetryOnAuth,
  });
}

export function useFrameworks(repoId: string) {
  return useQuery({
    queryKey: codeHealthKeys.frameworks(repoId),
    queryFn: () => getFrameworks(repoId),
    enabled: Boolean(repoId),
    staleTime: 60_000,
    retry: dontRetryOnAuth,
  });
}

export function useSonarAnalysis(repoId: string) {
  return useQuery({
    queryKey: codeHealthKeys.sonar(repoId),
    queryFn: () => getSonarAnalysis(repoId),
    enabled: Boolean(repoId),
    staleTime: 60_000,
    retry: dontRetryOnAuth,
  });
}

export function useQualityFindings(
  repoId: string,
  opts?: {
    rule_id?: string;
    kind?: string;
    severity?: string;
    status?: string;
    file_path_contains?: string;
    limit?: number;
    offset?: number;
  },
) {
  return useQuery({
    queryKey: codeHealthKeys.qualityFindings(repoId, opts),
    queryFn: () => listQualityFindings(repoId, opts),
    enabled: Boolean(repoId),
    staleTime: 30_000,
    retry: dontRetryOnAuth,
  });
}
